import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Printer, ArrowLeft, Star, Heart, Sprout, MessageCircle } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const BOOK_COVER = "https://customer-assets.emergentagent.com/job_farm-tales-store/artifacts/d0h3ch73_image.png";
const CALLIE_FACE = "https://customer-assets.emergentagent.com/job_farm-tales-store/artifacts/0z4pacu5_Callie%20Curious%20Look.jpg";
const FSK_LOGO = "https://customer-assets.emergentagent.com/job_farm-tales-store/artifacts/kyjajpew_1000080225%20%281%29.png";

const PAYMENT_LABELS = {
  paypal: "PayPal",
  cash_pickup: "Cash on Pickup",
  bimpay: "BimPay",
  firstpay: "FirstPay (CIBC Caribbean)"
};

const DELIVERY_LABELS = {
  pickup: "Barbados Pickup",
  delivery: "Barbados Delivery",
  rural: "Barbados Rural Delivery"
};

const STATUS_LABELS = {
  pending_payment: "Pending Payment",
  paid: "Paid",
  shipped: "Shipped",
  delivered: "Delivered",
  cancelled: "Cancelled"
};

const InvoicePage = ({ adminMode = false }) => {
  const { orderNumber } = useParams();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const url = adminMode
          ? `${API}/api/admin/orders/${orderNumber}`
          : `${API}/api/orders/${orderNumber}`;
        const { data } = await axios.get(url, { withCredentials: adminMode });
        setOrder(data);
      } catch (err) {
        setError(err.response?.status === 404 ? "Order not found" : "Failed to load order");
      }
    };
    fetchOrder();
  }, [orderNumber, adminMode]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]">
        <div className="text-center">
          <p className="text-2xl font-bold text-[#E63946] mb-4">{error}</p>
          <Link to={adminMode ? "/admin/orders" : "/"} className="text-[#1D3557] font-bold underline">← Back</Link>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]">
        <div className="w-16 h-16 border-4 border-[#E63946] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const invoiceDate = new Date(order.created_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  const addr = order.address || {};
  const hasAddress = addr.line1 || addr.city || addr.country;

  // WhatsApp share — strip non-digits from phone, fall back to wa.me without number
  // so the admin can pick the contact from their address book.
  const sanitizedPhone = (order.phone || "").replace(/\D/g, "");
  const invoiceUrl = `${window.location.origin}/invoice/${order.order_number}`;
  const firstName = (order.customer_name || "").trim().split(/\s+/)[0] || "there";
  const waMessage = `Hi ${firstName}! 👋

Here's your invoice from FarmSmart Kids:
Order #${order.order_number}
Total: $${order.total.toFixed(2)} ${order.currency}
Status: ${STATUS_LABELS[order.status] || order.status}

View / print it here:
${invoiceUrl}

Thanks for supporting Callie & the gang! 🐄`;
  const waBase = sanitizedPhone ? `https://wa.me/${sanitizedPhone}` : "https://wa.me/";
  const waLink = `${waBase}?text=${encodeURIComponent(waMessage)}`;

  // Callie watermark — repeating pattern using data URL-style positioning
  const callieWatermark = {
    backgroundImage: `url("${CALLIE_FACE}")`,
    backgroundSize: "120px 120px",
    backgroundRepeat: "repeat",
    opacity: 0.06
  };

  return (
    <>
      <style>{`
        @media print {
          @page { size: A4; margin: 10mm; }
          body { background: white !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          header, nav, footer, [data-no-print="true"] { display: none !important; }
          .invoice-page { background: white !important; padding: 0 !important; }
          .invoice-shell { box-shadow: none !important; border: 3px solid #1D3557 !important; }
        }
      `}</style>

      <div className="min-h-screen bg-[#FDFBF7] py-8 px-4 md:px-8 invoice-page">
        <div className="max-w-4xl mx-auto">
          {/* Toolbar */}
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6" data-no-print="true">
            <Link
              to={adminMode ? "/admin/orders" : "/"}
              className="text-[#1D3557] font-bold inline-flex items-center gap-2 hover:text-[#E63946]"
              data-testid="back-link"
            >
              <ArrowLeft className="w-5 h-5" /> {adminMode ? "Back to Orders" : "Back to Home"}
            </Link>
            <div className="flex flex-wrap items-center gap-3">
              {adminMode && (
                <a
                  href={waLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebe5b] text-white font-bold px-5 py-3 rounded-xl border-3 border-[#1D3557] shadow-[3px_3px_0px_0px_rgba(29,53,87,1)] hover:shadow-[5px_5px_0px_0px_rgba(29,53,87,1)] hover:-translate-y-0.5 transition-all"
                  data-testid="whatsapp-send-btn"
                  title={sanitizedPhone ? `Send to ${order.phone}` : "Pick a contact in WhatsApp"}
                >
                  <MessageCircle className="w-5 h-5" />
                  Send via WhatsApp
                </a>
              )}
              <button
                onClick={() => window.print()}
                className="btn-primary inline-flex items-center gap-2"
                data-testid="print-btn"
              >
                <Printer className="w-5 h-5" /> Print / Save as PDF
              </button>
            </div>
          </div>

          {/* Invoice */}
          <div className="invoice-shell tactile-card overflow-hidden bg-white relative" data-testid="invoice">
            {/* Callie watermark layer */}
            <div className="absolute inset-0 pointer-events-none" style={callieWatermark} aria-hidden="true"></div>

            {/* All invoice content sits above the watermark */}
            <div className="relative">
              {/* Header banner */}
              <div className="relative bg-gradient-to-br from-[#2A9D8F]/15 via-[#E9C46A]/20 to-[#457B9D]/15 p-6 md:p-8 border-b-4 border-[#1D3557]">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
                  {/* Logo + slogan */}
                  <div className="flex items-start gap-4">
                    <div className="bg-white rounded-2xl p-1.5 border-4 border-[#1D3557] flex-shrink-0" style={{ width: 110, height: 110 }}>
                      <img
                        src={FSK_LOGO}
                        alt="FarmSmart Kids logo"
                        className="w-full h-full object-contain rounded-xl"
                      />
                    </div>
                    <div>
                      <h1 className="text-2xl md:text-3xl font-black leading-tight text-[#1D3557]" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                        FarmSmart Kids<br/>
                        <span className="text-base md:text-lg font-bold">International Inc.</span>
                      </h1>
                      <p className="text-[#1D3557] font-bold text-sm mt-2 italic">
                        Read. <span className="text-[#2A9D8F]">Learn.</span> <span className="text-[#E9C46A]">Grow.</span> <span className="text-[#E63946]">Together.</span>
                      </p>
                    </div>
                  </div>

                  {/* Invoice tag */}
                  <div className="text-right">
                    <div className="inline-block bg-[#1D3557] text-white px-6 py-3 rounded-2xl border-4 border-[#E9C46A] -rotate-2 shadow-lg">
                      <p className="text-3xl md:text-4xl font-black tracking-wider" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                        INVOICE
                      </p>
                    </div>
                    <div className="bg-[#E9C46A]/30 rounded-xl p-3 mt-3 border-2 border-[#E9C46A]">
                      <p className="text-xs text-[#1D3557] font-bold leading-snug">
                        Thanks for supporting a smarter, more sustainable future for all! <Heart className="inline w-3 h-3 text-[#E63946]" fill="#E63946" />
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bill-to + Invoice meta */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6 md:p-8 border-b-2 border-[#1D3557]/10 bg-white/85">
                <div>
                  <span className="inline-block px-3 py-1 rounded-full bg-[#E63946] text-white font-bold text-xs uppercase tracking-wider mb-3">Bill To</span>
                  <p className="font-bold text-[#1D3557] text-lg">{order.customer_name}</p>
                  <p className="text-[#1D3557] font-medium">{order.email}</p>
                  {order.phone && <p className="text-[#1D3557] font-medium">{order.phone}</p>}
                  {hasAddress && (
                    <div className="text-[#1D3557] font-medium mt-2 text-sm">
                      {addr.line1 && <p>{addr.line1}</p>}
                      {addr.line2 && <p>{addr.line2}</p>}
                      {(addr.city || addr.state_province) && <p>{[addr.city, addr.state_province].filter(Boolean).join(", ")} {addr.postal_code || ""}</p>}
                      {addr.country && <p className="font-bold">{addr.country}</p>}
                    </div>
                  )}
                </div>

                <div className="space-y-2 text-sm">
                  <div className="grid grid-cols-2 gap-2 items-center">
                    <span className="font-bold text-[#1D3557]/70">Invoice #</span>
                    <span className="font-mono font-bold text-[#1D3557]">{order.order_number}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 items-center">
                    <span className="font-bold text-[#1D3557]/70">Date</span>
                    <span className="font-bold text-[#1D3557]">{invoiceDate}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 items-center">
                    <span className="font-bold text-[#1D3557]/70">Status</span>
                    <span className="font-bold text-[#E63946]">{STATUS_LABELS[order.status] || order.status}</span>
                  </div>
                  {order.payment_method && (
                    <div className="grid grid-cols-2 gap-2 items-center">
                      <span className="font-bold text-[#1D3557]/70">Payment</span>
                      <span className="font-bold text-[#1D3557]">{PAYMENT_LABELS[order.payment_method] || order.payment_method}</span>
                    </div>
                  )}
                  {order.delivery_method && (
                    <div className="grid grid-cols-2 gap-2 items-center">
                      <span className="font-bold text-[#1D3557]/70">Delivery</span>
                      <span className="font-bold text-[#1D3557]">{DELIVERY_LABELS[order.delivery_method] || order.delivery_method}</span>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-2 items-center">
                    <span className="font-bold text-[#1D3557]/70">Author</span>
                    <span className="font-bold text-[#1D3557]">Terata B.</span>
                  </div>
                </div>
              </div>

              {/* Items table */}
              <div className="p-6 md:p-8 bg-white/85">
                <div className="overflow-hidden rounded-2xl border-3 border-[#1D3557]">
                  <div className="grid grid-cols-12 bg-[#2A9D8F] text-white text-xs uppercase font-bold tracking-wider py-3 px-4">
                    <div className="col-span-6">Item</div>
                    <div className="col-span-2 text-center">Qty</div>
                    <div className="col-span-2 text-right">Unit Price</div>
                    <div className="col-span-2 text-right">Total</div>
                  </div>
                  {(order.items && order.items.length > 0 ? order.items : [{
                    sku: order.product_name,
                    name: order.product_name,
                    unit_price: order.unit_price,
                    quantity: order.quantity,
                    line_total: order.unit_price * order.quantity
                  }]).map((it, idx) => (
                    <div key={idx} className="grid grid-cols-12 items-center gap-3 p-4 bg-white border-t border-[#1D3557]/10" data-testid={`invoice-item-${idx}`}>
                      <div className="col-span-6 flex items-center gap-3">
                        {idx === 0 && (
                          <img src={BOOK_COVER} alt="" className="w-12 h-12 rounded-lg border-2 border-[#1D3557] object-cover hidden sm:block" />
                        )}
                        <div>
                          <p className="font-bold text-[#1D3557] text-sm leading-tight">{it.name}</p>
                          {idx === 0 && (
                            <p className="text-xs text-[#1D3557]/70 italic">From the FarmSmart Kids collection</p>
                          )}
                        </div>
                      </div>
                      <div className="col-span-2 text-center font-bold text-[#1D3557]">{it.quantity}</div>
                      <div className="col-span-2 text-right font-bold text-[#1D3557]">${(it.unit_price || 0).toFixed(2)}</div>
                      <div className="col-span-2 text-right font-bold text-[#1D3557]">${((it.line_total !== undefined ? it.line_total : (it.unit_price * it.quantity)) || 0).toFixed(2)}</div>
                    </div>
                  ))}
                </div>

                {/* Totals */}
                <div className="flex justify-end mt-6">
                  <div className="w-full md:w-1/2 bg-[#E9C46A]/30 rounded-2xl p-5 border-2 border-[#E9C46A] space-y-2">
                    <div className="flex justify-between text-[#1D3557] font-medium">
                      <span>Subtotal</span>
                      <span>${order.subtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-[#1D3557] font-medium">
                      <span>Shipping &amp; Handling</span>
                      <span>{order.shipping === 0 ? "FREE" : `$${order.shipping.toFixed(2)}`}</span>
                    </div>
                    <div className="border-t-2 border-[#1D3557]/20 pt-2 flex justify-between font-black text-lg">
                      <span className="text-[#1D3557]">TOTAL DUE</span>
                      <span className="text-[#E63946]">${order.total.toFixed(2)} {order.currency}</span>
                    </div>
                  </div>
                </div>

                {/* Author note + payment terms */}
                <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-[#2A9D8F] font-bold uppercase tracking-wider text-xs mb-2 flex items-center gap-1">
                      <Sprout className="w-4 h-4" /> A note from the author
                    </p>
                    <p className="text-[#1D3557] font-medium italic leading-relaxed text-sm" style={{ fontFamily: "'Quicksand', sans-serif" }}>
                      "When you invest in this series, you're not just reading a story — you're growing agri-education and sustainability."
                    </p>
                  </div>

                  <div className="bg-[#457B9D]/10 rounded-2xl p-4 border-2 border-[#457B9D]/30 flex items-start gap-3">
                    <Star className="w-6 h-6 text-[#E9C46A] flex-shrink-0 mt-0.5" fill="#E9C46A" />
                    <p className="text-sm text-[#1D3557] font-medium leading-snug">
                      Please remit payment within 3–5 days (for bank transfers only). Thank you! <Sprout className="inline w-4 h-4 text-[#2A9D8F]" />
                    </p>
                  </div>
                </div>

                {order.notes && (
                  <div className="mt-6 p-3 bg-[#1D3557]/5 rounded-xl border-l-4 border-[#1D3557]">
                    <p className="text-xs uppercase font-bold tracking-wider text-[#1D3557]/60 mb-1">Notes</p>
                    <p className="text-sm text-[#1D3557] whitespace-pre-line">{order.notes}</p>
                  </div>
                )}
              </div>

              {/* Payment info footer */}
              <div className="bg-[#1D3557] text-white p-6 md:p-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-[#E9C46A] font-bold text-xs uppercase tracking-wider mb-2">Payment Info <Heart className="inline w-3 h-3 text-[#E63946]" fill="#E63946" /></p>
                    <div className="space-y-1 text-sm font-medium">
                      <p><span className="text-[#E9C46A] font-bold">PayPal:</span> <a href="https://www.paypal.com/ncp/payment/BRGHYT9K5USCG" className="underline">paypal.me/farmsmartkids</a></p>
                      <p><span className="text-[#E9C46A] font-bold">Cash on Pickup:</span> Available — bring exact amount in USD or BBD equivalent</p>
                      <p><span className="text-[#E9C46A] font-bold">BimPay / FirstPay (CIBC Caribbean):</span> Contact info@farmsmartkids.com for details</p>
                    </div>
                  </div>
                  <div className="md:text-right">
                    <div className="inline-flex items-center gap-2 bg-[#2A9D8F] text-white rounded-full px-5 py-3 font-black text-sm border-2 border-[#E9C46A]" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                      <span>🐄</span> YOU'RE A-MOO-ZING! <span>🐄</span>
                    </div>
                    <p className="text-xs text-white/80 mt-3 italic">Thank you for supporting independent authors &amp; sustainable futures!</p>
                  </div>
                </div>

                <div className="border-t border-white/20 mt-6 pt-4 flex flex-wrap gap-3 justify-between text-xs text-white/80 font-medium">
                  <span>@callie_detective</span>
                  <span>www.farmsmartkids.com</span>
                  <span className="text-[#E9C46A] font-bold">Together, we can do amazing things! <Heart className="inline w-3 h-3 text-[#E63946]" fill="#E63946" /></span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default InvoicePage;
